import { ACTION_ARGS, Arg, Metadata, pick } from '../../../utils/spds/metadata';
import { and } from 'ember-truth-helpers';
import AlertBannerContainer from 'ember-simplepractice/components/spds/feedback/alert-banner/container';
import ImageCrop from 'ember-simplepractice/components/shared/image-crop';
import SpdsModal from 'ember-simplepractice/components/spds/modal/standard';
import Super from '../core/base.gjs';
import ToastContainer from 'ember-simplepractice/components/spds/feedback/toast/container';
import styles from './image-crop.module.scss';

const METADATA = Metadata.define({
  description: 'A modal that allows cropping of profile images',
  extends: [
    pick(ACTION_ARGS, [
      'onBeforeClose',
      {
        name: 'onClose',
        description: 'Action to be called when the modal is closed',
        required: true,
      },
    ]),
  ],
  meta: {
    file: Arg.object({ description: 'The image file to be cropped' }),
    minImgHeight: Arg.number({ description: 'Required minimum height for the image' }),
    minImgWidth: Arg.number({ description: 'Required minimum width for the image' }),
    onConfirm: Arg.action({ description: 'Action to be called when the image is confirmed' }),
    recommendedMinImgHeight: Arg.number({
      description: 'Recommended minimum height for the image',
    }),
    recommendedMinImgWidth: Arg.number({ description: 'Recommended minimum width for the image' }),
    viewportBaseSize: Arg.number({ default: 200, description: 'The base size for the viewport' }),
    viewportHeightRatio: Arg.number({
      default: 1,
      description: 'The ratio of the viewport height',
    }),
    viewportWidthRatio: Arg.number({ default: 1, description: 'The ratio of the viewport width' }),
  },
  name: 'Image Crop',
});

export default class SpdsModalImageCrop extends Super {
  static metadata = this.setMetadata(METADATA, import.meta.url);
  baseClass = 'spds-modal-image-crop';
  styles = styles;

  get bannerContainerId() {
    return this.composeSubcomponentId('banner-container');
  }

  get viewportBaseSize() {
    return this.argOrDefault('viewportBaseSize');
  }

  get viewportHeightRatio() {
    return this.argOrDefault('viewportHeightRatio');
  }

  get viewportWidthRatio() {
    return this.argOrDefault('viewportWidthRatio');
  }

  <template>
    {{#if @file}}
      <SpdsModal
        @id={{this.id}}
        @onClose={{@onClose}}
        @onBeforeClose={{@onBeforeClose}}
        @closeOnClickOutside={{@closeOnClickOutside}}
        @alignItems="center"
        class={{this.classes}}
        data-test-spds-modal-image-crop
        as |M|
      >
        <M.Header @title="Edit image" />
        <M.Body>
          <AlertBannerContainer @id={{this.bannerContainerId}} />
          <ImageCrop
            @file={{@file}}
            @isStrictRecommendation={{@isStrictRecommendation}}
            @minImgHeight={{@minImgHeight}}
            @minImgWidth={{@minImgWidth}}
            @onClose={{@onClose}}
            @onConfirm={{@onConfirm}}
            @recommendedMinImgHeight={{@recommendedMinImgHeight}}
            @recommendedMinImgWidth={{@recommendedMinImgWidth}}
            @viewportBaseSize={{this.viewportBaseSize}}
            @viewportHeightRatio={{this.viewportHeightRatio}}
            @viewportWidthRatio={{this.viewportWidthRatio}}
            as |ic|
          >
            <ic.banner />
            {{#if (and ic.imgWidth ic.imgHeight)}}
              <ic.preview />
              {{#if ic.isAllowedToZoom}}
                <ic.range-input />
              {{/if}}
            {{/if}}
            <ic.actions @excludePwStyle={{@excludePwStyle}} />
          </ImageCrop>
          <ToastContainer @local={{true}} />
        </M.Body>
      </SpdsModal>
    {{/if}}
  </template>
}

export const MODAL_IMAGE_CROP_METADATA = SpdsModalImageCrop.metadata;
